import { Input, Text } from "@solomon-labs/design/components/ui";
import React, { useEffect, useState } from "react";

import { AccountTypeBadge } from "../../components/Yaas/AccountTypeBadge";
import { useSolanaAddressVerification } from "../../hooks/useSolanaAddressVerification";
import {
    type AddressChain,
    EvmAddress,
    SolanaAddress
} from "../../utils/address";

import type { FieldIssueState } from "./types";

interface RewardsDestinationFieldProps {
    value: string;
    onChange: (value: string) => void;
    state: FieldIssueState;
    /** Connected wallet address — shown as the input's placeholder
     *  text (pre-filled appearance) and used as the implicit
     *  "default" when the user submits with an empty input. */
    connectedWallet: string;
    /** When `false`, hides the "Rewards Destination (Defaults Same
     *  Account)" label above the input. Useful when surrounding
     *  context already identifies the field. Defaults to `true`. */
    showLabel?: boolean;
    /** Override the input's placeholder text. Defaults to
     *  `connectedWallet` (the apply-form behavior — placeholder reads
     *  as a pre-filled default). Useful when the field is being used
     *  to enter a brand-new address rather than confirming the
     *  default ("Enter Account Address"). */
    placeholder?: string;
    /** When set, live-validates `value` against the chain's address
     *  shape and surfaces an error border for non-empty invalid input.
     *  Defaults to "solana". Uses `isShape` (Tier 1 only) — *not*
     *  `isWallet` — because legitimate destinations include PDAs
     *  (Squads multisig vaults, DAO treasuries, etc.) which are
     *  intentionally off-curve. The PDA vs wallet vs token-account
     *  distinction is surfaced as a soft status hint via the Tier 3
     *  on-chain check, not as a hard error border. The explicit
     *  `state` prop still wins so a caller can override (e.g.
     *  RPC-rejected addresses). */
    chain?: AddressChain;
    /** Hide the inline Tier-3 (on-chain) status line. Set when the
     *  caller wants to render the verification status in its own
     *  layout slot — typically by calling
     *  `useSolanaAddressVerification(value)` itself and placing the
     *  result wherever it belongs (alongside an action button, etc).
     *  The hook still runs internally regardless, so duplicating it
     *  at the call site is safe (React dedupes via the same trimmed
     *  input). Defaults to `false`. */
    hideOnChainStatus?: boolean;
    /** Replace the generic account status and badge with a richer verification result. */
    verificationContent?: React.ReactNode;
}

/**
 * Rewards Destination field — Solana address input.
 *
 * The connected wallet renders as the input's placeholder text — it
 * looks pre-filled but is actually empty, so on submit an unedited
 * field is treated as "use the default." On focus, the placeholder
 * clears so the empty input is unambiguously ready for typing. Live
 * base58 validation surfaces errors for non-empty invalid input.
 */
export function RewardsDestinationField({
    value,
    onChange,
    state,
    connectedWallet,
    showLabel = true,
    placeholder,
    chain = "solana",
    hideOnChainStatus = false,
    verificationContent
}: RewardsDestinationFieldProps): React.ReactElement {
    const [isFocused, setIsFocused] = useState(false);
    const placeholderText = placeholder ?? connectedWallet;
    // Caller-supplied `state` wins; if it's the neutral "default" we
    // upgrade to "error" for non-empty input that fails the chain's
    // shape check. Empty stays "default" so the field doesn't scream
    // at the user before they've typed anything. We deliberately
    // accept PDAs here (off-curve 32-byte addresses) since multisig
    // vaults and treasuries are legitimate destinations — the
    // wallet/PDA distinction surfaces via the Tier 3 status text,
    // not a hard error.
    const trimmed = value.trim();
    const shapeValid =
        trimmed.length === 0 ||
        (chain === "solana"
            ? SolanaAddress.isShape(trimmed)
            : EvmAddress.isShape(trimmed));

    // Debounce the INVALID feedback (error border + "must be valid."
    // message) by ~1s so the field reads neutral while the user is
    // still typing. `settled` is false during the 1s window after the
    // last keystroke; once it flips true (the user paused), invalid
    // display is allowed to show. The success path, the badge, and the
    // on-chain verification are NOT gated here — they already have
    // their own debounce inside the hook.
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedValue(value), 1000);
        return (): void => window.clearTimeout(t);
    }, [value]);
    const settled = debouncedValue === value;

    // Tier 3 — debounced on-chain lookup. The hook gates internally
    // on `isShape`, so EVM input (and locally-invalid Solana input)
    // never fires an RPC. Disabled entirely when chain isn't Solana.
    // 1s debounce: wait until the user has paused typing for a full
    // second before the check fires, so the badge/status doesn't
    // flicker in mid-type.
    const verification = useSolanaAddressVerification(value, {
        enabled: chain === "solana",
        debounceMs: 1000
    });
    // The field's error border lights up when EITHER the local shape
    // check fails OR the on-chain check decides the address is
    // hard-invalid (mint, wrong-mint token account) — but only once the
    // user has stopped typing for ~1s (`settled`). While typing, we fall
    // back to the caller-supplied `state` so the border reads neutral
    // (the form's own `default` stays `default`, no red mid-type).
    // Caller-supplied non-`default` state still wins immediately so
    // RPC-driven overrides from outside the field continue to work.
    const showInvalid = settled && (!shapeValid || verification.invalid);
    const rawState: FieldIssueState =
        state === "default" && showInvalid ? "error" : state;
    // Suppress the red error BORDER while the user is still typing (not
    // `settled`) — even when the caller-supplied `state` is already "error"
    // (the form's immediate draft validation). The border, like the message,
    // only turns red once they've paused ~1s. Non-error states (success,
    // warning) pass through immediately.
    const effectiveState: FieldIssueState =
        !settled && rawState === "error" ? "default" : rawState;
    const onChainToneClass =
        verification.tone === "error"
            ? "text-error-base"
            : verification.tone === "success"
              ? "text-success-base"
              : verification.tone === "warning"
                ? "text-warning-base"
                : verification.tone === "muted"
                  ? "text-foreground-imprint"
                  : "text-foreground-neutral";

    return (
        <div className="flex flex-col gap-3">
            {showLabel && (
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-base"
                >
                    Rewards Destination
                </Text>
            )}
            {/* Input + helper text grouped tight (gap-1) so the hint
                line reads as a footnote attached to the input rather
                than another item in the outer gap-3 stack. */}
            <div className="flex flex-col gap-1">
                {/* DS Input, lightbar rail. Replaces a hand-rolled
                    bordered shell whose border colours duplicated what
                    `state` already drives; `aria-invalid` comes with
                    `state="error"` so it is no longer set by hand. */}
                <Input
                    interaction="lightbar"
                    size="md"
                    name="rewardsDestination"
                    data-sim-cursor-key="destination"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={isFocused ? "" : placeholderText}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    state={
                        effectiveState === "error"
                            ? "error"
                            : effectiveState === "warning"
                              ? "warning"
                              : effectiveState === "success"
                                ? "success"
                                : undefined
                    }
                />
                {/* Status row — on-chain status text (left) + the
                    account-type badge (right), on ONE line. ALWAYS
                    rendered so it reserves a constant `min-h-7` (28px):
                    the status/badge fade in and out *inside* this fixed
                    slot, so the field — and everything below it (ToS,
                    Verify CTA) — never changes height when the check
                    resolves. The badge used to sit on its own line above
                    the input, which both pushed content down and grew the
                    form; this keeps it compact AND height-stable. Status
                    text is suppressed when the caller surfaces it
                    elsewhere (`hideOnChainStatus`) or while the local
                    check fails (the error border already speaks). */}
                <div className="flex min-h-7 items-center justify-between gap-2">
                    {verificationContent ?? (
                        <>
                            {!hideOnChainStatus &&
                            settled &&
                            trimmed.length > 0 &&
                            !shapeValid ? (
                                // Validation error shares this slot with the success
                                // status — so error + success render in the SAME spot,
                                // never two places. (Replaces the form's old separate
                                // "Account addresses must be valid." message line.)
                                <Text
                                    variant="fine-print"
                                    render={<span />}
                                    className="text-error-base"
                                >
                                    Account addresses must be valid.
                                </Text>
                            ) : !hideOnChainStatus &&
                              verification.text &&
                              shapeValid ? (
                                <Text
                                    variant="fine-print"
                                    render={<span />}
                                    className={onChainToneClass}
                                >
                                    {verification.text}
                                </Text>
                            ) : (
                                <span aria-hidden="true" />
                            )}
                            {verification.accountType && (
                                <AccountTypeBadge
                                    type={verification.accountType}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
